import React, { createContext, useState, useContext, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { lightTheme, darkTheme } from './index';

// Create a theme context
const ThemeContext = createContext({
  currentTheme: 'dark',
  setTheme: () => {},
});

// Custom hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Check if theme was previously set in localStorage
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'dark';
  });

  // Get the theme object from MUI based on current theme state
  const getThemeObject = () => {
    switch(currentTheme) {
      case 'light':
        return lightTheme;
      default:
        return darkTheme;
    }
  };

  const theme = getThemeObject();

  // Set theme function
  const setTheme = (themeName) => {
    setCurrentTheme(themeName);
  };

  // Helper functions for compatibility with existing code
  const isDarkMode = currentTheme !== 'light';
  const toggleTheme = () => {
    // Cycle through themes: light -> dark -> neon -> light
    if (currentTheme === 'light') setCurrentTheme('dark');
    else setCurrentTheme('light');
  };

  // Update localStorage when theme changes
  useEffect(() => {
    localStorage.setItem('theme', currentTheme);
    // Toggle the theme class via classList so we don't clobber other
    // body classes (e.g. ``sidebar-collapsed``) that other contexts add.
    document.body.classList.remove('light-mode', 'dark-mode');
    document.body.classList.add(`${currentTheme}-mode`);
  }, [currentTheme]);

  // Context value
  const contextValue = {
    currentTheme,
    setTheme,
    isDarkMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider; 